import { FC } from "react";

import { useTranslation } from "react-i18next";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../Logo";
import NavigationProgress from "@features/NavigationProgress";
import AccountMenu from "./components/AccountMenu";
import HideOnScroll from "./components/HideOnScroll";
import CartButton from "./components/CartButton";
import NotificationsMenu from "./components/NotificationsMenu";

import { brandColorTokens } from "@constants";
import { appLayoutDimensions } from "../../AppLayout.constants";

import { IHeaderProperties } from "./Header.types";

const Header: FC<IHeaderProperties> = ({
  isMenuButtonShown,
  isAccountNavigationShown,
  isHiddenOnScroll,
  onMenuToggle,
}) => {
  const { t } = useTranslation();

  const appBar = (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: brandColorTokens.background.inverted,
        color: brandColorTokens.text.bodyInverted,
        boxShadow: `inset 0 -1px 0 ${brandColorTokens.line.light}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: appLayoutDimensions.headerHeight,
          px: 2,
        }}
      >
        {isMenuButtonShown && (
          <IconButton
            color="inherit"
            aria-label={t("appHeader.menu.title")}
            onClick={onMenuToggle}
            data-cy="app-layout-menu-button"
            data-amp-default-track={true}
            data-amp-event-type="global.iconButton.toggleDrawer"
            sx={{ ml: { xs: -1, md: 0 } }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ display: "flex", flexShrink: 0, pl: isMenuButtonShown ? 0 : 1 }}>
          <Logo isCompactOnPhone={isAccountNavigationShown} isInverted={true} />
        </Box>
        {isAccountNavigationShown && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", minWidth: 0 }}>
            <CartButton />
            <NotificationsMenu />
            <AccountMenu />
          </Box>
        )}
      </Box>
      <NavigationProgress />
    </AppBar>
  );

  return isHiddenOnScroll ? <HideOnScroll>{appBar}</HideOnScroll> : appBar;
};

export default Header;
